import React, { useMemo } from 'react';
import {
  ItemListOptionMetadata,
  ItemListOptionSection,
} from '@/contexts/item-context-types';
import { ItemMenuOperationsContent } from './orders/item-menu-operations-content';
import { ItemSubtitle } from './item-subtitle';
import { LIST_ITEM_VERTICAL_PADDING } from './list-item';
import { PixelRatio, StyleSheet, View } from 'react-native';
import { RecyclerList } from './recycler-list';
import { TYPOGRAPHY } from '@/styles';

const MAX_VARIANT_PACK_OPTIONS_TO_DISPLAY = 4;
const TAG_PRINTING_OPTION_LINE_HEIGHT = 24;
const TAG_PRINTING_ESTIMATED_HEIGHT =
  TAG_PRINTING_OPTION_LINE_HEIGHT * PixelRatio.getFontScale() +
  LIST_ITEM_VERTICAL_PADDING * 2;

interface TagPrintingVariantPackSelectionProps {
  onSelectVariantPackUUID: (variantPackUUID: string) => void;
  variantPacks: ItemListOptionMetadata[];
}

export const VariantPackSelection: React.FC<
  TagPrintingVariantPackSelectionProps
> = ({ onSelectVariantPackUUID, variantPacks }) => {
  const variantPackSections = useMemo<ItemListOptionSection[]>(() => {
    return [
      {
        data: variantPacks.map((vp) => ({
          metadata: vp,
          title: `${vp.packUnit} · ${vp.externalID}`,
          value: vp.variantPackUUID,
        })),
        key: null,
      },
    ];
  }, [variantPacks]);

  return (
    <ItemMenuOperationsContent
      hideCloseButton
      onClose={() => {}}
      operationName="print_tag_select_variant_pack"
    >
      <View style={styles.base}>
        <View
          style={{
            height:
              TAG_PRINTING_ESTIMATED_HEIGHT *
              Math.min(
                variantPacks.length,
                MAX_VARIANT_PACK_OPTIONS_TO_DISPLAY,
              ),
          }}
        >
          <RecyclerList
            alwaysBounceVertical={false}
            estimatedItemSize={TAG_PRINTING_ESTIMATED_HEIGHT}
            listItemProps={{
              listItemStyle: styles.listItem,
              listTitleStyle: [TYPOGRAPHY.listTitle, styles.listTitle],
              renderMainContent: ({ metadata }) => {
                const vp = metadata as ItemListOptionMetadata;
                return (
                  <ItemSubtitle
                    currencyCode={vp.currencyCode}
                    displayString={vp.packUnit}
                    modifierString={vp.externalID ?? undefined}
                    showInlineModifiers
                    unitCount={vp.unitCount}
                  />
                );
              },
            }}
            listItemSeparatorProps={{
              style: styles.listItemSeparator,
            }}
            onSelect={(item) => onSelectVariantPackUUID(item.value.toString())}
            sections={variantPackSections}
            showSeparator
            testID="tag-printing-variant-pack-list"
          />
        </View>
      </View>
    </ItemMenuOperationsContent>
  );
};

const styles = StyleSheet.create({
  base: {
    flexDirection: 'column',
  },
  listItem: {
    paddingHorizontal: 0,
  },
  listItemSeparator: {
    marginHorizontal: 0,
  },
  listTitle: {
    paddingVertical: 16,
  },
});
